import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  backgroundColor: '#1e1e1e',
  borderRadius: '12px',
  overflow: 'hidden',
  border: '2px solid transparent',
  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    border: '2px solid rgba(103, 126, 234, 0.5)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
  },
  '& video': {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    borderRadius: '12px',
    transform: 'none',
    backfaceVisibility: 'hidden',
    willChange: 'auto'
  }
}));

const VideoGrid = ({ 
  participants, 
  getParticipantVideoRef, 
  getParticipantId,
  room 
}) => {
  const theme = useTheme();

  if (!participants || participants.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%',
        backgroundColor: '#1a1a1a',
        borderRadius: '12px'
      }}>
        <Box sx={{ 
          textAlign: 'center',
          p: 4,
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderRadius: '12px',
          backdropFilter: 'blur(10px)'
        }}>
        </Box>
      </Box>
    );
  }

  const getGridConfig = () => {
    const count = participants.length;
    // All participants stack vertically (full width)
    if (count === 1) return { xs: 12, sm: 12, md: 12, height: '100%' };
    if (count === 2) return { xs: 12, sm: 12, md: 12, height: '48%' };
    if (count <= 4) return { xs: 12, sm: 12, md: 12, height: '23%' };
    if (count <= 6) return { xs: 12, sm: 12, md: 12, height: '15%' };
    return { xs: 12, sm: 12, md: 12, height: '150px' }; // Fixed height for many participants
  };

  const gridConfig = getGridConfig();

  return (
    <Box sx={{ 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      p: 1,
      overflow: participants.length > 6 ? 'auto' : 'hidden',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '3px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '3px',
        '&:hover': {
          background: 'rgba(255, 255, 255, 0.4)',
        },
      },
    }}>
      {participants.map((participant, idx) => {
        const participantId = getParticipantId(participant);
        const isLocal = participant === room?.localParticipant;
        
        return (
          <Box key={participantId} sx={{ 
            height: gridConfig.height,
            minHeight: '120px',
            flex: participants.length === 1 ? 1 : '0 0 auto'
          }}>
            <VideoContainer sx={{ 
              height: '100%',
              backgroundColor: '#1e1e1e',
              position: 'relative'
            }}>
              <video 
                ref={getParticipantVideoRef(participantId)} 
                autoPlay 
                playsInline 
                muted={isLocal}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
              
              {/* Participant Label */}
              <Box sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 500,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {isLocal ? 'You' : `Participant ${idx + 1}`}
              </Box>
            </VideoContainer>
          </Box>
        );
      })}
    </Box>
  );
};

export default VideoGrid; 